import { useGetProcessorJobsQuery } from '@consigli/hooks';
import { ProcessorJob, ProcessorJobStatus } from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState, useCallback } from 'react';

import { Badge, DataTable, ObjectDetailView } from '@/components';
import { processorJobStatusMapping } from '@/utils/processor-job-status-mapping';
import { formatTimestamp } from '@/utils/timestamp-converting';

import { PipelineActionButton } from './pipeline-action-button';
import { StagesExpandedContent } from './stages-expanded-content';

interface PipelinesTabProps {
  projectId: number;
  packageId: string;
}

/**
 * Generic pipelines view for any service type (document packages, building packages, layout optimizations).
 * Displays a DataTable with all pipelines, expandable rows showing stages, and nested tasks.
 * Also includes controls for starting and stopping pipelines.
 */
export const PipelinesTab = ({ projectId, packageId }: PipelinesTabProps) => {
  const [selectedPipeline, setSelectedPipeline] = useState<ProcessorJob | null>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);

  const { data, isLoading } = useGetProcessorJobsQuery({
    projectId,
    packageId,
  });

  const handleViewDetails = useCallback((pipeline: ProcessorJob) => {
    setSelectedPipeline(pipeline);
    setIsDetailViewOpen(true);
  }, []);

  const handleCloseDetailView = useCallback((open: boolean) => {
    setIsDetailViewOpen(open);
    if (!open) {
      setSelectedPipeline(null);
    }
  }, []);

  const columns: ColumnDef<ProcessorJob>[] = useMemo(() => {
    const ProcessorJobStatusBadge = ({ status }: { status: ProcessorJobStatus }) => {
      const config = processorJobStatusMapping[status];
      return <Badge text={config.text} variant={config.variant} />;
    };

    return [
      {
        header: 'Pipeline Name',
        accessorKey: 'pipelineName',
        cell: ({ getValue }) => {
          const pipelineName = getValue() as string;
          return <span>{formatEnumToReadableText(pipelineName)}</span>;
        },
      },
      {
        header: 'Created At',
        accessorKey: 'createdAt',
        cell: ({ getValue }) => {
          const createdAt = getValue() as string;
          return <span>{formatTimestamp(createdAt)}</span>;
        },
      },
      {
        header: 'Updated At',
        accessorKey: 'updatedAt',
        cell: ({ getValue }) => {
          const updatedAt = getValue() as string;
          return <span>{formatTimestamp(updatedAt)}</span>;
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ getValue }) => {
          const status = getValue() as ProcessorJobStatus;
          return <ProcessorJobStatusBadge status={status} />;
        },
      },
      {
        header: 'Action',
        cell: ({ row }) => {
          return (
            <PipelineActionButton
              pipeline={row.original}
              projectId={projectId}
              onViewDetails={handleViewDetails}
            />
          );
        },
      },
    ];
  }, [projectId, handleViewDetails]);

  return (
    <>
      <div className="w-full">
        <DataTable
          columns={columns}
          data={data || []}
          isLoading={isLoading}
          expandOnRowClick
          expandedContent={(pipeline) => (
            <StagesExpandedContent projectId={projectId} jobId={pipeline.id} />
          )}
        />
      </div>

      {selectedPipeline && (
        <ObjectDetailView
          object={selectedPipeline}
          objectType="Pipeline"
          summary={`Pipeline "${selectedPipeline.pipelineName}" details including status, timestamps, and configuration.`}
          isOpen={isDetailViewOpen}
          onOpenChange={handleCloseDetailView}
        />
      )}
    </>
  );
};
